import React from "react";
import { NavLink, useLocation } from "react-router-dom";
import {
  LayoutGrid, Users, Building2, Handshake, Send, Terminal, Radar,
  ChevronsLeft, ChevronsRight, Search, Bell, Settings, CalendarDays, ListChecks, Bot, Plug, Mail, Timer,
} from "lucide-react";

const mainNav = [
  { to: "/", label: "Home", icon: LayoutGrid },
  { to: "/people", label: "People", icon: Users },
  { to: "/companies", label: "Companies", icon: Building2 },
  { to: "/deals", label: "Deals", icon: Handshake },
  { to: "/social-profiles", label: "Social Profiles", icon: Radar },
  { to: "/mailbox", label: "Mailbox", icon: Mail },
];

const workflowNav = [
  { to: "/sequences", label: "Sequences", icon: Send },
  { to: "/content", label: "Content & Social", icon: CalendarDays },
  { to: "/tasks", label: "Agent Tasks", icon: ListChecks },
  { to: "/automations", label: "Automations", icon: Timer },
  { to: "/integrations", label: "Integrations", icon: Plug },
  { to: "/developers", label: "Buddy Terminal", icon: Terminal },
];

export default function Sidebar({ collapsed, setCollapsed }) {
  const location = useLocation();
  const isActive = (to) => (to === "/" ? location.pathname === "/" : location.pathname.startsWith(to));

  const Item = ({ to, label, icon: Icon }) => (
    <NavLink
      to={to}
      className={`group flex items-center gap-2.5 rounded-md px-2 py-[7px] text-[13.5px] font-medium transition-colors ${
        isActive(to)
          ? "bg-[hsl(240,5%,93%)] text-[hsl(240,6%,10%)]"
          : "text-[hsl(240,4%,42%)] hover:bg-[hsl(240,5%,96%)] hover:text-[hsl(240,6%,10%)]"
      }`}
    >
      <Icon className="h-[17px] w-[17px] shrink-0" strokeWidth={2} />
      {!collapsed && <span className="truncate">{label}</span>}
    </NavLink>
  );

  return (
    <aside
      className={`${collapsed ? "w-[60px]" : "w-[236px]"} shrink-0 h-full border-r border-[hsl(240,6%,91%)] bg-[hsl(240,20%,99%)] flex flex-col transition-[width] duration-200`}
    >
      {/* Workspace */}
      <div className="flex items-center gap-2 px-3 h-[52px] border-b border-[hsl(240,6%,92%)]">
        <div className="h-7 w-7 rounded-md brand-bg text-white flex items-center justify-center shrink-0">
          <Bot className="h-4 w-4" />
        </div>
        {!collapsed && (
          <div className="min-w-0 flex-1">
            <div className="text-[13px] font-semibold leading-tight truncate">BuddyOS</div>
            <div className="text-[10.5px] text-[hsl(240,4%,55%)] leading-tight">by Tokenbuds</div>
          </div>
        )}
        <button
          onClick={() => setCollapsed(!collapsed)}
          className="text-[hsl(240,4%,55%)] hover:text-[hsl(240,6%,10%)] p-1 rounded hover:bg-[hsl(240,5%,94%)]"
        >
          {collapsed ? <ChevronsRight className="h-4 w-4" /> : <ChevronsLeft className="h-4 w-4" />}
        </button>
      </div>

      {!collapsed && (
        <div className="px-3 pt-3">
          <button className="w-full flex items-center gap-2 rounded-md border border-[hsl(240,6%,90%)] bg-white px-2.5 py-[7px] text-[13px] text-[hsl(240,4%,50%)] hover:bg-[hsl(240,5%,97%)] transition-colors">
            <Search className="h-[15px] w-[15px]" />
            <span>Search…</span>
            <span className="ml-auto text-[11px] font-mono bg-[hsl(240,5%,95%)] rounded px-1.5 py-0.5">⌘K</span>
          </button>
        </div>
      )}

      <nav className="flex-1 overflow-y-auto px-2.5 pt-3 space-y-0.5">
        {mainNav.map((n) => <Item key={n.to} {...n} />)}
        <div className={`${collapsed ? "mx-1" : "mx-2"} my-3 h-px bg-[hsl(240,6%,92%)]`} />
        {!collapsed && <div className="px-2 pb-1 text-[10.5px] font-semibold uppercase tracking-wider text-[hsl(240,4%,60%)]">Workflows</div>}
        {workflowNav.map((n) => <Item key={n.to} {...n} />)}
      </nav>

      <div className="border-t border-[hsl(240,6%,92%)] p-2.5 space-y-0.5">
        <button className="w-full flex items-center gap-2.5 rounded-md px-2 py-[7px] text-[13.5px] font-medium text-[hsl(240,4%,42%)] hover:bg-[hsl(240,5%,96%)] transition-colors">
          <Bell className="h-[17px] w-[17px]" />
          {!collapsed && <span>Notifications</span>}
        </button>
        <button className="w-full flex items-center gap-2.5 rounded-md px-2 py-[7px] text-[13.5px] font-medium text-[hsl(240,4%,42%)] hover:bg-[hsl(240,5%,96%)] transition-colors">
          <Settings className="h-[17px] w-[17px]" />
          {!collapsed && <span>Settings</span>}
        </button>
      </div>
    </aside>
  );
}
